import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { MessageThread } from "../../features/messages/models/MessageThread";

interface ThreadListProps {
  threads: MessageThread[];
  activeThreadId?: string;
  /** Where a row leads, e.g. `/messages/{id}`. */
  linkTo: (thread: MessageThread) => string;
  /** The threads are being fetched: with none yet the list stays quiet and marks itself busy. */
  loading?: boolean;
}

/** Hairline rows: title and time, then the preview; "Muted" for a muted thread and the unread count in the accent. */
const ThreadList = ({ threads, activeThreadId, linkTo, loading = false }: ThreadListProps) => {
  const { t, i18n } = useTranslation();
  const formatTime = (date: Date) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "short", timeStyle: "short" }).format(date);

  if (threads.length === 0) {
    // No rows and no text: the page's own line speaks for an empty or failed load.
    return <div data-testid="thread-list-empty" aria-busy={loading || undefined} />;
  }

  return (
    <ul className="divide-y divide-grey-90 dark:divide-primary-20">
      {threads.map((thread) => (
        <li key={thread.id}>
          <Link
            to={linkTo(thread)}
            aria-current={thread.id === activeThreadId ? "true" : undefined}
            className={`block px-2 py-3 hover:bg-grey-95 dark:hover:bg-primary-20 ${
              thread.id === activeThreadId ? "bg-grey-95 dark:bg-primary-20" : ""
            }`}
          >
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate font-semibold text-grey-20 dark:text-white">{thread.title}</span>
              {thread.lastMessageAt && (
                <span className="shrink-0 text-xs text-grey-40 dark:text-grey-80">
                  {formatTime(thread.lastMessageAt)}
                </span>
              )}
            </div>
            <div className="flex items-baseline justify-between gap-3">
              <span className="truncate text-sm text-grey-40 dark:text-grey-80">
                {thread.lastMessagePreview ?? ""}
              </span>
              {thread.isMuted && (
                <span className="shrink-0 text-xs text-grey-40 dark:text-grey-80">{t("messages.muted")}</span>
              )}
              {thread.unreadCount > 0 && (
                <span className="shrink-0 text-sm font-semibold tabular-nums text-primary-40 dark:text-primary-90">
                  {thread.unreadCount > 9 ? "9+" : thread.unreadCount}
                </span>
              )}
            </div>
          </Link>
        </li>
      ))}
    </ul>
  );
};

export default ThreadList;
